import { supabase } from './supabase';

export interface Asset {
  id: string;
  business_id: string;
  name: string;
  asset_type: string | null;
  description: string | null;
  status: 'active' | 'inactive';
  not_bookable: boolean;
  image_url: string | null;
  unit_number: string | null;
  vehicle_year: number | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vin: string | null;
  schedule_availability: any;
  created_at: string;
  updated_at: string;
}

export interface CreateAssetPayload {
  business_id: string;
  name: string;
  asset_type?: string;
  description?: string;
  status?: string;
  not_bookable?: boolean;
  image_url?: string;
  unit_number?: string;
  vehicle_year?: number;
  vehicle_make?: string;
  vehicle_model?: string;
  vin?: string;
}

export interface UpdateAssetPayload {
  name?: string;
  asset_type?: string | null;
  description?: string | null;
  status?: string;
  not_bookable?: boolean;
  image_url?: string | null;
  unit_number?: string | null;
  vehicle_year?: number | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  vin?: string | null;
}

export async function getAssets(businessId: string): Promise<Asset[]> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const { data, error } = await supabase
    .from('assets')
    .select('*')
    .eq('business_id', businessId)
    .order('name', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getAsset(assetId: string): Promise<Asset | null> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const { data, error } = await supabase
    .from('assets')
    .select('*')
    .eq('id', assetId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function createAsset(payload: CreateAssetPayload): Promise<Asset> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const { data, error } = await supabase
    .from('assets')
    .insert({
      business_id: payload.business_id,
      name: payload.name,
      asset_type: payload.asset_type || null,
      description: payload.description || null,
      status: payload.status || 'active',
      not_bookable: payload.not_bookable ?? false,
      image_url: payload.image_url || null,
      unit_number: payload.unit_number || null,
      vehicle_year: payload.vehicle_year || null,
      vehicle_make: payload.vehicle_make || null,
      vehicle_model: payload.vehicle_model || null,
      vin: payload.vin || null,
    })
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

export async function updateAsset(assetId: string, updates: UpdateAssetPayload): Promise<Asset> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const { data, error } = await supabase
    .from('assets')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', assetId)
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

export async function deleteAsset(assetId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const { error } = await supabase
    .from('assets')
    .delete()
    .eq('id', assetId);

  if (error) throw error;
}
